import { createSlice } from "@reduxjs/toolkit";

import {
  addGrade,
  deleteGrade,
  fetchGrades,
  updateGrade,
} from "../thunks/gradesThunks";

export interface Grade {
  grade_id: string;
  name: string;
  alias_1: string | null;
  alias_2: string | null;
  alias_3: string | null;
  required_vapor: boolean;
}

interface GradesState {
  gradesList: Grade[];
  loading: boolean;
  error: string | null;
  isDataFetched: boolean;
}

const initialState: GradesState = {
  gradesList: [],
  loading: false,
  error: null,
  isDataFetched: false,
};

export const gradesSlice = createSlice({
  name: "grades",
  initialState,
  reducers: {
    resetGradesDataFetched: (state) => {
      state.isDataFetched = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchGrades.pending, (state) => {
        state.loading = true;
        state.isDataFetched = true;
        state.error = null;
      })
      .addCase(fetchGrades.fulfilled, (state, action) => {
        state.gradesList = action.payload ?? [];
        state.loading = false;
      })
      .addCase(fetchGrades.rejected, (state, action) => {
        state.error = action.payload as string;
        state.isDataFetched = false;
        state.loading = false;
      });

    builder
      .addCase(addGrade.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addGrade.fulfilled, (state, action) => {
        state.loading = false;
        state.gradesList = [action.payload, ...state.gradesList];
      })
      .addCase(addGrade.rejected, (state) => {
        state.loading = false;
        state.error = "Failed to add grade. Please try again.";
      });

    builder
      .addCase(updateGrade.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateGrade.fulfilled, (state, action) => {
        state.loading = false;
        const updated = action.payload as Grade;
        if (updated) {
          state.gradesList = state.gradesList.map((g) =>
            g.grade_id === updated.grade_id ? { ...g, ...updated } : g
          );
        }
      })
      .addCase(updateGrade.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || "Failed to update grade. Please try again.";
      });

    builder
      .addCase(deleteGrade.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteGrade.fulfilled, (state, action) => {
        state.loading = false;
        state.gradesList = state.gradesList.filter(
          (g) => g.grade_id !== action.payload.id
        );
      })
      .addCase(deleteGrade.rejected, (state) => {
        state.loading = false;
        state.error = "Failed to delete grade. Please try again.";
      });
  },
});

export const { resetGradesDataFetched } = gradesSlice.actions;
export default gradesSlice.reducer;
